import createClient from "openapi-fetch";

import { API_URL } from "@/constants/env";
import { type paths } from "@/libs/openapi/schema";
import { queryClient } from "@/main";

export class HttpException extends Error {
  response?: Response;
  isGlobal: boolean;

  constructor({
    message,
    response,
    isGlobal
  }: {
    message?: string,
    response?: Response,
    /** Wether the error should be handled by global error handler */
    isGlobal?: boolean
  } = { message: 'Something went wrong' }) {
    super(message);
    this.name = "HttpException";
    this.response = response;
    this.isGlobal = isGlobal ?? true;
  }
}

export const client = createClient<paths>({ baseUrl: API_URL });

const ignoreUnauthorized: (keyof paths)[] = [
  '/api/v1/auth/google/login',
  '/api/v1/auth/google/callback',
  '/api/v1/auth/login',
  '/api/v1/auth/logout',
];

const refreshPath: keyof paths = '/api/v1/auth/refresh';

let refreshTokenPromise: Promise<void> | null = null;

client.use({
  onRequest({ request }) {
    request.headers.set("Authorization", `Bearer ${localStorage.getItem('access_token')}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (!response.ok && response.status === 401) {
      const url = new URL(request.url);

      if (url.pathname === refreshPath) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        queryClient.clear();
        window.location.href = '/sign-in';
      }

      if (ignoreUnauthorized.includes(url.pathname as keyof paths)) {
        return response;
      }

      if (refreshTokenPromise) {
        await refreshTokenPromise;
      } else {
        refreshTokenPromise = (async () => {
          try {
            const refreshResponse = await client.POST("/api/v1/auth/refresh", {
              body: {
                refresh_token: localStorage.getItem('refresh_token')!,
              },
            });

            if (refreshResponse.data) {
              localStorage.setItem('access_token', refreshResponse.data.access_token);
              localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
            }
          } finally {
            refreshTokenPromise = null;
          }
        })();

        await refreshTokenPromise;
      }

      const retry = await fetch(new Request(request));
      if (retry.ok) {
        return retry;
      }

      response = retry;
    }

    return response;
  },
});

export * from "./schema";
